import { Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';

import { ChangeEvent, FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql } from '../../../../../gql';
import { useApiMutation, useApiQuery } from '../../../../../query/hooks';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import config from '../../../../../config/config';
import { useSnackbar } from '../../../../../store/snackbar';

const getDashboardMetrics = graphql(`
	query getDashboardMetricsList {
		listDashboardMetric {
			contentMetric {
				id
			}
		}
	}
`);

const createDashboardMetricMutation = graphql(`
	mutation CreateDashboardMetric($data: DashboardMetricCreateInput!) {
		createDashboardMetric(data: $data) {
			ok
			errorMessage
		}
	}
`);
const updateDashboardMetricMutation = graphql(`
	mutation UpdateDashboardMetric($contentMetricId: UUID!, $data: DashboardMetricUpdateInput!) {
		updateDashboardMetric(by: { contentMetric: { id: $contentMetricId } }, data: $data) {
			ok
			errorMessage
		}
	}
`);
const deleteDashboardMetricMutation = graphql(`
	mutation DeleteDashboardMetric($contentMetricId: UUID!) {
		deleteDashboardMetric(by: { contentMetric: { id: $contentMetricId } }) {
			ok
			errorMessage
		}
	}
`);

const sourceDrawer_getContentMetricQuery = graphql(`
	query sourceDrawer_getContentMetricQuery($contentMetricId: UUID!) {
		getContentMetric(by: { id: $contentMetricId }) {
			hideFromPlanning
		}
	}
`);

const sourceDrawer_updateContentMetricMutation = graphql(`
	mutation sourceDrawer_updateContentMetricMutation($contentMetricId: UUID!, $value: Boolean!) {
		updateContentMetric(by: { id: $contentMetricId }, data: { hideFromPlanning: $value }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<{
	contentMetricId: string;
}>;

const SourceDrawerTab: FC<Props> = ({ contentMetricId }) => {
	const intl = useIntl();
	const { data } = useApiQuery(getDashboardMetrics);
	const { data: contentMetricData } = useApiQuery(sourceDrawer_getContentMetricQuery, { variables: { contentMetricId } });
	const { openSnackbar } = useSnackbar();
	const { mutate: createDashboardMetric } = useApiMutation(createDashboardMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.createDashboardMetric.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'metrics-widget.source.dashboard-add.success' }),
					error: intl.formatMessage({ id: 'metrics-widget.source.dashboard-add.error' }),
				},
				errorMessage: result.createDashboardMetric.errorMessage,
				openSnackbar,
			});
		},
	});

	const { mutate: updateDashboardMetric } = useApiMutation(updateDashboardMetricMutation);
	const { mutate: deleteDashboardMetric } = useApiMutation(deleteDashboardMetricMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.deleteDashboardMetric.ok,
				toastMessages: {
					success: intl.formatMessage({ id: 'metrics-widget.source.dashboard-delete.success' }),
					error: intl.formatMessage({ id: 'metrics-widget.source.dashboard-delete.error' }),
				},
				errorMessage: result.deleteDashboardMetric.errorMessage,
				openSnackbar,
			});
		},
	});

	const { mutate: updateContentMetric } = useApiMutation(sourceDrawer_updateContentMetricMutation);

	const handleDashboardMetric = (event: ChangeEvent<HTMLInputElement>) => {
		const { checked, value } = event.target;

		if (checked) {
			createDashboardMetric({
				data: {
					contentMetric: { connect: { id: value } },
					displayType: 'currentValue',
					dashboard: { connect: { id: config.dashboardId } },
					order: 0,
				},
			});
		} else {
			deleteDashboardMetric({ contentMetricId: value });
		}
	};

	const handleHideOnPlanning = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		updateContentMetric({ contentMetricId: value, value: !ishiddenOnPlanning });
	};

	const dashboardMetrics = data?.listDashboardMetric ?? [];
	const isLimitReached = dashboardMetrics.length >= 13;

	const [isChecked, setIsChecked] = useState(false);
	const [ishiddenOnPlanning, setIshiddenOnPlanning] = useState(false);

	useEffect(() => {
		if (data?.listDashboardMetric != null) {
			setIsChecked(() => data.listDashboardMetric.some((metric) => metric.contentMetric?.id === contentMetricId));
		}
	}, [contentMetricId, data?.listDashboardMetric]);

	useEffect(() => {
		if (contentMetricData?.getContentMetric?.hideFromPlanning != null) {
			setIshiddenOnPlanning(contentMetricData.getContentMetric.hideFromPlanning);
		}
	}, [contentMetricData]);

	useEffect(() => {
		dashboardMetrics.forEach((dashboard, idx) => {
			updateDashboardMetric({
				contentMetricId: dashboard.contentMetric?.id ?? '',
				data: { displayType: idx < 3 ? 'currentValue' : 'history', order: idx },
			});
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dashboardMetrics]);

	return (
		<Stack>
			<FormControlLabel
				sx={{ margin: 0 }}
				value={contentMetricId}
				control={<Checkbox />}
				label={<FormattedMessage id={'metrics-widget.source.show-on-dashboard'} />}
				disabled={isLimitReached && !isChecked}
				checked={isChecked}
				onChange={(event) => {
					setIsChecked(!isChecked);
					handleDashboardMetric(event as ChangeEvent<HTMLInputElement>);
				}}
			/>
			<FormControlLabel
				sx={{ margin: 0 }}
				value={contentMetricId}
				control={<Checkbox />}
				label={<FormattedMessage id={'metrics-widget.source.hide-on-planning'} />}
				checked={ishiddenOnPlanning}
				onChange={(event) => {
					setIshiddenOnPlanning(!ishiddenOnPlanning);
					handleHideOnPlanning(event as ChangeEvent<HTMLInputElement>);
				}}
			/>
			{isLimitReached && (
				<Typography variant={'caption'} color={(theme) => theme.palette.error.main}>
					<FormattedMessage id={'metrics-widget.source.dashboard-limit-reached'} />
				</Typography>
			)}
		</Stack>
	);
};

export { SourceDrawerTab };
